import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Dashboard,
  Campaign,
  Payments,
  BarChart,
  Info,
  Group,
  Help,
  ExpandLess,
  ExpandMore,
  Quiz,
  Support,
  MenuBook,
  TrendingUp,
  Assessment,
  AccountBalanceWallet,
  MoneyRounded,
  MoneyTwoTone,
  People,
  BikeScooter,
} from "@mui/icons-material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";

const drawerWidth = 280;

const Sidebar = ({ user, settings, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedItem, setSelectedItem] = useState();
  const [expandedItems, setExpandedItems] = useState({});
  const { t, i18n } = useTranslation();

  const handleItemClick = (itemName) => {
    setSelectedItem(itemName);
    if (isMobile) {
      onMobileClose();
    }
  };

  const handleExpandClick = (itemName) => {
    setExpandedItems((prev) => ({
      ...prev,
      [itemName]: !prev[itemName],
    }));
  };

  const sidebarItems = [
    { name: t("sidebar_dashboard"), icon: <Dashboard />, path: "/dashboard" },
    { name: t("sidebar_drivers"), icon: <BikeScooter />, path: "/drivers" },
    {
      name: t("sidebar_statement"),
      icon: <AttachMoneyIcon />,
      path: "/statement",
    },
    { name: t("sidebar_payouts"), icon: <Payments />, path: "/payouts" },
    {
      name: t("sidebar_bank"),
      icon: <MoneyRounded />,
      path: "/bank-details",
    },
    {
      name: t("sidebar_referrals"),
      icon: <People />,
      path: "/my-referrals",
    },
    {
      name: t("sidebar_promotions"),
      icon: <Campaign />,
      path: "/promotions",
    },
  ];

  const renderSidebarItem = (item, isSubitem = false) => {
    const isSelected =
      selectedItem === item.name ||
      window.location.pathname.includes(item.path);
    const isExpanded = expandedItems[item.name];

    return (
      <React.Fragment key={item.name}>
        <ListItem disablePadding className="sidebar-list-item">
          <ListItemButton
            selected={isSelected}
            onClick={() => {
              handleItemClick(item.name);
              if (item.expandable) {
                handleExpandClick(item.name);
              }
              if (item.path) {
                navigate(item.path);
              }
            }}
            className={`sidebar-list-item-button ${
              isSubitem ? "sidebar-list-item-button--subitem" : ""
            } ${isSelected ? "sidebar-list-item-button--selected" : ""}`}
          >
            {item.icon && (
              <ListItemIcon
                className={`sidebar-list-item-icon ${
                  isSelected ? "sidebar-list-item-icon--selected" : ""
                }`}
              >
                {item.icon}
              </ListItemIcon>
            )}
            <ListItemText
              primary={item.name}
              className={`sidebar-list-item-text ${
                isSelected ? "sidebar-list-item-text--selected" : ""
              }`}
            />
            {item.expandable && (
              <Box
                className={`sidebar-expand-icon ${
                  isSelected ? "sidebar-expand-icon--selected" : ""
                } ${isExpanded ? "sidebar-expand-icon--expanded" : ""}`}
              >
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>

        {item.expandable && item.subItems && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding className="sidebar-sub-list">
              {item.subItems.map((subItem) => renderSidebarItem(subItem, true))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const MobileWallet = () => (
    <Box className="mobile-wallet">
      <Box className="mobile-wallet__content">
        <AccountBalanceWallet className="mobile-wallet__icon" />
        <Box>
          <Typography variant="body2" className="mobile-wallet__label">
            Wallet Balance
          </Typography>
          <Typography variant="h6" className="mobile-wallet__amount">
            {settings[5].value} {user?.balance ?? "0.00"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const drawerContent = (
    <Box className="sidebar-drawer-content">
      <List
        className={`sidebar-main-list ${
          isMobile ? "sidebar-main-list--mobile" : ""
        }`}
      >
        {sidebarItems.map((item) => renderSidebarItem(item))}
      </List>

      {isMobile && (
        <>
          <Divider className="mobile-wallet-divider" />
          <MobileWallet />
        </>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{
          keepMounted: true,
        }}
        classes={{
          paper: "sidebar-drawer-paper sidebar-drawer-paper--mobile",
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      className="sidebar-drawer"
      classes={{
        paper: "sidebar-drawer-paper sidebar-drawer-paper--desktop",
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

const mapStateToProps = (state) => ({
  user: state.user.user,
  settings: state.settings.settings,
});

export default connect(mapStateToProps)(Sidebar);
